import React, { useMemo, useEffect, useState } from "react";
import { Field, reduxForm, FieldArray } from "redux-form";
import {
  Typography,
  Divider,
  Grid,
  IconButton,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import RemoveCircleOutlineIcon from "@material-ui/icons/RemoveCircleOutline";
import TuneIcon from "@material-ui/icons/Tune";
import {
  TextInput,
  SelectInput,
  CheckboxInput,
  ToggleInput,
  AutocompleteSelectInput,
} from "../FormInputs";
import { useStyles } from "../FormSelection";
import { objToSelectOptions, listTimeZones } from "../../../helpers";

// Profile UUIDs are generated on the dashboard so the form can bind per-profile
// FC/RP/IT inputs to the profile's id locally before save (and so a single PATCH
// can create a profile and its parameters atomically).
const makeProfileId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    const value = char === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

const ALL_DEPTHS = [10, 20, 30, 40, 50, 60, 70, 80, 90];

const validate = (values) => {
  const errors = {};
  const requiredInput = [
    "name",
    "client_id",
    "farm",
    "field_type",
    "crop_type",
  ];
  requiredInput.forEach((input) => {
    if (!values[input]) {
      errors[input] = "Required";
    }
  });

  if (values.sensors && values.sensors.length) {
    const sensorsArrayErrors = [];
    values.sensors.forEach((sensor, index) => {
      const sensorErrors = {};
      const requiredSensorInput = ["metric_id", "logger_id", "ref"];
      requiredSensorInput.forEach((input) => {
        if (!sensor || !sensor[input]) {
          sensorErrors[input] = "Required";
          sensorsArrayErrors[index] = sensorErrors;
        }
      });
    });
    if (sensorsArrayErrors.length) {
      errors.sensors = sensorsArrayErrors;
    }
  }
  return errors;
};

const RenderSensors = ({
  fields,
  meta: { error, submitFailed },
  loggers,
  metrics,
}) => {
  const loggerOptions = useMemo(
    () => objToSelectOptions(loggers, "id", "logger_id"),
    [loggers]
  );

  const metricsOptions = useMemo(
    () => objToSelectOptions(metrics, "id", "name"),
    [metrics]
  );
  const metricsWithConfig =
    metrics &&
    metrics.reduce((arr, item) => {
      if (item.ref === "run_off_in") {
        arr.push(item.id);
      }
      return arr;
    }, []);

  return (
    <>
      <Grid container>
        <Grid item xs>
          <Typography variant="h5">Sensors</Typography>
        </Grid>
        <Grid item>
          <IconButton
            aria-label="Add"
            size="small"
            type="button"
            title="Add"
            color="primary"
            onClick={() => fields.push({})}
          >
            <AddCircleIcon />
          </IconButton>
        </Grid>
      </Grid>
      {submitFailed && error && <span>{error}</span>}
      {fields.map((sensor, index, ...rest) => {
        const { metric_id } = rest[0].get(index);
        return (
          <Grid key={index} container alignItems="center">
            <Grid item xs>
              <Grid container>
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item md>
                      <Field
                        name={`${sensor}.metric_id`}
                        id={`${sensor}.metric_id`}
                        component={SelectInput}
                        label="Metric"
                        options={metricsOptions}
                        normalize={(s) => parseInt(s)}
                      />
                    </Grid>
                    <Grid item md>
                      <Field
                        name={`${sensor}.logger_id`}
                        id={`${sensor}.logger_id`}
                        component={AutocompleteSelectInput}
                        label="Logger ID"
                        options={loggerOptions}
                        normalize={(s) => parseInt(s)}
                      />
                    </Grid>
                    <Grid item md>
                      <Field
                        name={`${sensor}.ref`}
                        id={`${sensor}.ref`}
                        component={TextInput}
                        type="text"
                        label="API Node Reference"
                      />
                    </Grid>
                    {metricsWithConfig.length &&
                      metricsWithConfig.includes(metric_id) && (
                        <Grid item md>
                          <Field
                            name={`${sensor}.multiplier`}
                            id={`${sensor}.multiplier`}
                            component={TextInput}
                            type="number"
                            min={1}
                            label="Multiplier"
                          />
                        </Grid>
                      )}
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Grid container spacing={2}></Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <IconButton
                aria-label="Remove"
                size="small"
                type="button"
                title="Remove"
                onClick={() => fields.remove(index)}
              >
                <RemoveCircleOutlineIcon />
              </IconButton>
            </Grid>
          </Grid>
        );
      })}
    </>
  );
};

/**
 * Renders a clickable chip-set for selecting depths. Backed by a redux-form
 * Field so the value lives at `<base>.depths` as an array of integers.
 */
const DepthChips = ({ input, depthsAvailable }) => {
  const selected = Array.isArray(input.value) ? input.value : [];
  const toggle = (d) => {
    const next = selected.includes(d)
      ? selected.filter((x) => x !== d)
      : [...selected, d].sort((a, b) => a - b);
    input.onChange(next);
  };
  return (
    <Grid container spacing={1}>
      {depthsAvailable.map((d) => (
        <Grid item key={d}>
          <Chip
            label={`${d}cm`}
            color={selected.includes(d) ? "primary" : "default"}
            onClick={() => toggle(d)}
            size="small"
          />
        </Grid>
      ))}
    </Grid>
  );
};

/**
 * Renders the list of rootzone profiles. Each row holds: name, depth chips,
 * field capacity / refill point / irrigation trigger and the full set of SMD
 * threshold inputs (SMD 5 … SMD 85, see SMD_PARAM_DEPTHS) per profile, plus the
 * automation toggle and hide toggle.
 *
 * The per-profile parameter values are stored locally on the profile object
 * (under `params.field_capacity` etc.) and `api/fields.ts` extracts them into
 * a `profile_parameters` payload at submit time so the backend creates
 * append-only Parameter rows with `rootzone_profile_id` set.
 */
// Soil-moisture-deficit threshold lines that can be configured per profile.
// Mirrors the smd_* parameter types supported by the API and the SMD line
// styling in ChartSoilClimate (5cm steps up to 85cm).
const SMD_PARAM_DEPTHS = [
  5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85,
];

// Count of SMD thresholds with a non-blank value, used for the row summary.
const countSmdParams = (params = {}) =>
  SMD_PARAM_DEPTHS.reduce((count, depth) => {
    const value = params[`smd_${depth}`];
    return value === undefined || value === null || value === ""
      ? count
      : count + 1;
  }, 0);

/**
 * Modal editor for a single profile's SMD threshold lines. Keeps the 17
 * smd_* inputs out of the main profile row — they live behind a button and
 * render in a compact grid only while the dialog is open. The redux-form
 * Field values persist in the store when the dialog closes (the Fields just
 * unmount), so this is purely a presentation change.
 */
const SmdParamsDialog = ({ open, onClose, profile, title }) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>SMD thresholds{title ? ` — ${title}` : ""}</DialogTitle>
    <DialogContent dividers>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Soil-moisture-deficit threshold lines in 5cm steps. Leave a depth blank
        to omit its line.
      </Typography>
      <Grid container spacing={2}>
        {SMD_PARAM_DEPTHS.map((depth) => (
          <Grid item xs={6} sm={4} md={3} key={depth}>
            <Field
              name={`${profile}.params.smd_${depth}`}
              id={`${profile}.params.smd_${depth}`}
              component={TextInput}
              type="number"
              label={`${depth}cm`}
            />
          </Grid>
        ))}
      </Grid>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} color="primary">
        Done
      </Button>
    </DialogActions>
  </Dialog>
);

const RenderProfiles = ({ fields, depthsAvailable }) => {
  // Index of the profile whose SMD dialog is open, or null when none is.
  const [smdDialogIndex, setSmdDialogIndex] = useState(null);
  const getProfiles = () =>
    Array.from({ length: fields.length }, (_item, index) => fields.get(index));

  const normalizeAutomationProfile = (profiles) => {
    const activeIndex = profiles.findIndex((p) => p?.active && !p?.archived);
    const fallbackIndex = profiles.findIndex((p) => !p?.archived);
    const selectedIndex = activeIndex >= 0 ? activeIndex : fallbackIndex;

    return profiles.map((profile, index) => ({
      ...profile,
      active: selectedIndex >= 0 && index === selectedIndex,
    }));
  };

  const replaceProfiles = (profiles) => {
    if (typeof fields.removeAll === "function") {
      fields.removeAll();
    } else {
      for (let i = fields.length - 1; i >= 0; i--) {
        fields.remove(i);
      }
    }
    profiles.forEach((profile) => fields.push(profile));
  };

  const setActive = (activeIndex) => {
    replaceProfiles(
      normalizeAutomationProfile(
        getProfiles().map((profile, index) => ({
          ...profile,
          active: index === activeIndex,
        }))
      )
    );
  };

  const setArchived = (index, archived) => {
    replaceProfiles(
      normalizeAutomationProfile(
        getProfiles().map((profile, profileIndex) =>
          profileIndex === index
            ? {
                ...profile,
                archived,
                active: archived ? false : profile?.active,
              }
            : profile
        )
      )
    );
  };

  const deleteProfile = (index) => {
    const current = fields.get(index);
    const profileName = current?.name?.trim();
    const label = profileName ? `"${profileName}"` : "this profile";
    const confirmed = window.confirm(
      `Delete ${label}? Save the field to apply this change.`
    );

    if (!confirmed) return;

    replaceProfiles(
      normalizeAutomationProfile(
        getProfiles().filter((_profile, profileIndex) => profileIndex !== index)
      )
    );
  };

  return (
    <>
      <Grid container alignItems="center">
        <Grid item xs>
          <Typography variant="h5">Rootzone Profiles</Typography>
          <Typography variant="body2" color="textSecondary">
            Optional depth groups for side-by-side moisture charts. Choose one
            profile for SMD, irrigation triggers, and automations.
          </Typography>
        </Grid>
        <Grid item>
          <IconButton
            aria-label="Add profile"
            size="small"
            title="Add profile"
            color="primary"
            onClick={() =>
              fields.push({
                id: makeProfileId(),
                name: "",
                depths: [...depthsAvailable],
                active: !getProfiles().some((p) => p?.active && !p?.archived),
                archived: false,
                params: {},
              })
            }
          >
            <AddCircleIcon />
          </IconButton>
        </Grid>
      </Grid>

      {fields.map((profile, index) => {
        const current = fields.get(index) ?? {};
        return (
          <Grid
            key={index}
            container
            spacing={2}
            alignItems="flex-start"
            style={{
              marginTop: 12,
              paddingBottom: 16,
              borderBottom:
                index < fields.length - 1
                  ? "1px solid rgba(0, 0, 0, 0.12)"
                  : undefined,
              opacity: current.archived ? 0.5 : 1,
            }}
          >
            <Grid item xs={12} md={6}>
              <Field
                name={`${profile}.name`}
                id={`${profile}.name`}
                component={TextInput}
                type="text"
                label="Name"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={1} alignItems="center" justify="flex-end">
                <Grid item>
                  <Field
                    name={`${profile}.active`}
                    id={`${profile}.active`}
                    component={ToggleInput}
                    label="Active"
                    disabled={current.archived}
                    onChange={(_e, checked) => {
                      if (checked) setActive(index);
                    }}
                  />
                </Grid>
                <Grid item>
                  <Field
                    name={`${profile}.archived`}
                    id={`${profile}.archived`}
                    component={CheckboxInput}
                    label="Hide"
                    onChange={(_e, checked) => setArchived(index, checked)}
                  />
                </Grid>
                <Grid item>
                  <IconButton
                    aria-label="Delete profile"
                    size="small"
                    title="Delete profile"
                    type="button"
                    color="secondary"
                    onClick={() => deleteProfile(index)}
                  >
                    <RemoveCircleOutlineIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">
                Depths
              </Typography>
              <Field
                name={`${profile}.depths`}
                id={`${profile}.depths`}
                component={DepthChips}
                depthsAvailable={depthsAvailable}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Field
                name={`${profile}.params.field_capacity`}
                id={`${profile}.params.field_capacity`}
                component={TextInput}
                type="number"
                label="FC"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Field
                name={`${profile}.params.refill_point`}
                id={`${profile}.params.refill_point`}
                component={TextInput}
                type="number"
                label="Refill"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Field
                name={`${profile}.params.irrigation_trigger`}
                id={`${profile}.params.irrigation_trigger`}
                component={TextInput}
                type="number"
                label="Trigger"
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<TuneIcon />}
                onClick={() => setSmdDialogIndex(index)}
              >
                {(() => {
                  const count = countSmdParams(current.params);
                  return count
                    ? `SMD thresholds (${count} set)`
                    : "SMD thresholds";
                })()}
              </Button>
            </Grid>
            <SmdParamsDialog
              open={smdDialogIndex === index}
              onClose={() => setSmdDialogIndex(null)}
              profile={profile}
              title={current.name}
            />
          </Grid>
        );
      })}
    </>
  );
};

const FieldForm = ({
  handleSubmit,
  error,
  valid,
  pristine,
  submitting,
  isValid,
  isWaiting,
  clients,
  initialValues,
  loggers,
  metrics,
}) => {
  useEffect(() => {
    isValid(!pristine && valid && !submitting);
    isWaiting(submitting);
  });
  const classes = useStyles();

  const frequencyOptions = {
    5: "5 minutes",
    15: "15 minutes",
  };

  return (
    <form noValidate onSubmit={handleSubmit}>
      <Field
        name="import"
        id="import"
        component={CheckboxInput}
        label="Import External API data"
      />
      <Field
        name="import_frequency"
        id="import_frequency"
        component={SelectInput}
        label="Import Frequency"
        options={frequencyOptions}
      />
      <Field
        component={TextInput}
        name="name"
        id="name"
        autoFocus={!initialValues}
        label="Name"
      />
      <Field
        component={SelectInput}
        name="client_id"
        id="client_id"
        label="Client"
        disabled={initialValues ? true : false}
        options={clients}
      />
      <Field component={TextInput} name="farm" id="farm" label="Farm" />
      <Field
        component={SelectInput}
        name="field_type"
        id="field_type"
        label="Field Type"
        options={{ substrate: "Substrate", soil: "Root Zone" }}
      />
      <Field
        component={TextInput}
        name="crop_type"
        id="crop_type"
        label="Crop Type"
      />
      <Field
        component={TextInput}
        name="valve_group"
        id="valve_group"
        label="Valve Group"
      />
      <Field
        component={SelectInput}
        name="timezone"
        id="timezone"
        label="Timezone"
        options={listTimeZones()}
      />
      <Field
        label="Automatic Drain Facility"
        name="auto_drain_facility"
        id="auto_drain_facility"
        component={ToggleInput}
      />
      <Field
        component={SelectInput}
        name="conductivity_line"
        id="conductivity_line"
        label="Conductivity Line"
        options={{
          default: "Default",
          10: "10 cm",
          20: "20 cm",
          30: "30 cm",
          40: "40 cm",
          50: "50 cm",
          60: "60 cm",
          70: "70 cm",
          80: "80 cm",
          90: "90 cm",
        }}
      />
      <Field
        component={TextInput}
        type="number"
        name="runoff_multiplication_factor"
        id="runoff_multiplication_factor"
        label="Run-off multiplication factor (used for app)"
      />
      <Field
        component={TextInput}
        type="number"
        name="chart_runoff_multiplication_factor"
        id="chart_runoff_multiplication_factor"
        label="Chart run-off multiplication factor"
      />
      <Field
        component={SelectInput}
        name="ec_division_factor"
        id="ec_division_factor"
        label="Ec Division Factor"
        valueType="number"
        options={{
          1: "1",
          10: "10",
          100: "100",
          1000: "1000",
          10000: "10000",
        }}
      />

      <Typography variant="body1" gutterBottom color="error">
        <b>{error}</b>
      </Typography>

      <Divider className={classes.divider} />

      <FieldArray
        name="sensors"
        component={RenderSensors}
        metrics={metrics}
        loggers={loggers}
      />

      <Divider className={classes.divider} />

      <FieldArray
        name="rootzone_profiles"
        component={RenderProfiles}
        depthsAvailable={ALL_DEPTHS}
      />

      <Divider className={classes.divider} />

      <Typography variant="h5" gutterBottom>
        Probe Labels
      </Typography>
      <Typography variant="body2" gutterBottom color="textSecondary">
        Override chart labels for multi-probe depths. Leave blank to use
        defaults.
      </Typography>

      <Typography variant="subtitle1" gutterBottom>
        Moisture
      </Typography>
      <Grid container spacing={2}>
        {["10", "20", "30", "40", "50", "60", "70", "80", "90"].map((d) => (
          <Grid item xs={12} md={4} key={`moisture_${d}`}>
            <Field
              component={TextInput}
              name={`metric_labels.moisture_${d}`}
              id={`metric_labels.moisture_${d}`}
              label={`${d}cm`}
              placeholder={`${d}cm`}
            />
          </Grid>
        ))}
      </Grid>

      <Typography variant="subtitle1" gutterBottom>
        Conductivity
      </Typography>
      <Grid container spacing={2}>
        {["10", "20", "30", "40", "50", "60", "70", "80", "90"].map((d) => (
          <Grid item xs={12} md={4} key={`vic_${d}`}>
            <Field
              component={TextInput}
              name={`metric_labels.vic_${d}`}
              id={`metric_labels.vic_${d}`}
              label={`${d}cm`}
              placeholder={`${d}cm`}
            />
          </Grid>
        ))}
      </Grid>

      <Typography variant="subtitle1" gutterBottom>
        Temperature
      </Typography>
      <Grid container spacing={2}>
        {["10", "20", "30", "40", "50", "60", "70", "80", "90"].map((d) => (
          <Grid item xs={12} md={4} key={`soil_temp_${d}`}>
            <Field
              component={TextInput}
              name={`metric_labels.soil_temp_${d}`}
              id={`metric_labels.soil_temp_${d}`}
              label={`${d}cm`}
              placeholder={`${d}cm`}
            />
          </Grid>
        ))}
      </Grid>
    </form>
  );
};

export default reduxForm({ form: "FieldForm", validate })(FieldForm);
